/**
 * Admin → Address Book (staff) — typed api-client calls + TanStack Query hooks
 * (E05-F03-T03).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport uses
 * the shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) and `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact same
 * shapes as the Fastify API.
 *
 * Endpoints (1:1 with the legacy n8n `manage_staff` action router — get_staff /
 * create_contact / update_contact / delete_contact, docs/source-n8n/reference/
 * Staff.md):
 *   GET    /admin/staff       → useStaff()           (get_staff)
 *   POST   /admin/staff       → useCreateContact()   (create_contact)
 *   PATCH  /admin/staff/:id   → useUpdateContact()   (update_contact)
 *   DELETE /admin/staff/:id   → useDeleteContact()   (delete_contact, HARD delete)
 *
 * The legacy SPA POSTed `{ command:'manage_staff', action, ... }` to one webhook;
 * the v2 transport is clean REST but the `command:'manage_staff'` tag is still
 * passed for the legacy-success unwrap to mirror the other admin tabs.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  staffListResponseSchema,
  staffMutationResponseSchema,
  staffDeleteResponseSchema,
  type StaffContactDTO,
  type CreateStaffContactInput,
  type UpdateStaffContactInput,
  type StaffMutationResponse,
  type StaffDeleteResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const STAFF_QUERY_KEY = ['admin', 'staff'] as const;

/** The legacy command tag carried on every staff request (manage_staff router). */
const STAFF_COMMAND = { command: 'manage_staff' } as const;

/** Fetch all active contacts (server orders category ASC, name ASC). */
async function fetchStaff(): Promise<StaffContactDTO[]> {
  const result = await apiClient.get<unknown>('/admin/staff', STAFF_COMMAND);
  const body = staffListResponseSchema.parse(unwrap(result));
  return body.contacts;
}

/** Create a contact (name+email required; category defaults to 'other' server-side). */
async function postContact(input: CreateStaffContactInput): Promise<StaffMutationResponse> {
  const result = await apiClient.post<unknown>('/admin/staff', input, STAFF_COMMAND);
  return staffMutationResponseSchema.parse(unwrap(result));
}

/** Partial-update a contact by id (only the supplied name/email/category are sent). */
async function patchContact(args: {
  id: number;
  patch: UpdateStaffContactInput;
}): Promise<StaffMutationResponse> {
  const result = await apiClient.patch<unknown>(
    `/admin/staff/${encodeURIComponent(String(args.id))}`,
    args.patch,
    STAFF_COMMAND,
  );
  return staffMutationResponseSchema.parse(unwrap(result));
}

/** HARD-delete a contact by id (legacy parity: always answers success). */
async function deleteContactReq(id: number): Promise<StaffDeleteResponse> {
  const result = await apiClient.delete<unknown>(
    `/admin/staff/${encodeURIComponent(String(id))}`,
    STAFF_COMMAND,
  );
  return staffDeleteResponseSchema.parse(unwrap(result));
}

/** List query — the address-book table. */
export function useStaff(): UseQueryResult<StaffContactDTO[], ApiResultError> {
  return useQuery<StaffContactDTO[], ApiResultError>({
    queryKey: STAFF_QUERY_KEY,
    queryFn: fetchStaff,
  });
}

/** Create mutation — invalidates the list so the new contact appears. */
export function useCreateContact(): UseMutationResult<
  StaffMutationResponse,
  ApiResultError,
  CreateStaffContactInput
> {
  const queryClient = useQueryClient();
  return useMutation<StaffMutationResponse, ApiResultError, CreateStaffContactInput>({
    mutationFn: postContact,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
    },
  });
}

/** Update mutation — invalidates the list so the edit re-renders. */
export function useUpdateContact(): UseMutationResult<
  StaffMutationResponse,
  ApiResultError,
  { id: number; patch: UpdateStaffContactInput }
> {
  const queryClient = useQueryClient();
  return useMutation<
    StaffMutationResponse,
    ApiResultError,
    { id: number; patch: UpdateStaffContactInput }
  >({
    mutationFn: patchContact,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
    },
  });
}

/** Delete mutation — invalidates the list so the removed row disappears. */
export function useDeleteContact(): UseMutationResult<StaffDeleteResponse, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<StaffDeleteResponse, ApiResultError, number>({
    mutationFn: deleteContactReq,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
    },
  });
}
